import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { RatingsDataService, RatingsColumnsService } from '../../services';
import { DialogRatingFormComponent } from './rating-form/rating-form.component';
import { DialogRatingDetailsComponent } from './rating-details/rating-details.component';
import { Rating, RatingsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-ratings',
  templateUrl: './ratings.component.html',
  styleUrls: ['./ratings.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class RatingsComponent implements OnInit, OnDestroy {

  private ratingAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private ratingEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private ratingDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public ratingToDelete: Rating = {};
  public ratings: Rating[] = [];
  public rating: Rating = {};
  public selectedRatings: Rating[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private ratingsDataService: RatingsDataService,
    private ratingsColumnsService: RatingsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('RatingsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Ratings - Management');
    this.primengConfig.ripple = true;
    this.getRatings();
  }

  ngOnDestroy(): void {
    console.log('RatingsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getRatings(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.ratingsDataService.getRatings(this.parameters).subscribe((response: RatingsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.ratings = response.results;
        } else {
          this.ratings = this.ratings.concat(response.results);
        }
        if (response.inlineCount > this.ratings.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getRatings();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('RATINGSNOTLOADED')});
    });
  }

  importRatings(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.ratingsDataService.addRatings(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('RATINGSCREATED')});
                if (proceed === j) {
                  this.getRatings();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('RATINGSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('RATINGSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.ratingsDataService.updateRatings(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('RATINGSUPDATED')});
                if (proceed === j) {
                  this.getRatings();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('RATINGSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('RATINGSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.ratingsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('ratings.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedRatings() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Ratings?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.ratingsDataService.deleteRatings(this.selectedRatings).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Ratings Deleted', life: 3000});
            this.getRatings();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Ratings not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Ratings not deleted'});
        });
        this.selectedRatings = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.ratings, this.columns, 'Ratings');
  }

  exportExcel() {
    ToolsService.exportExcel(this.ratings, this.columns, 'Ratings');
  }

  exportCsv() {
    ToolsService.exportCsv(this.ratings, this.columns, 'Ratings');
  }

  refresh() {
    this.ratingsDataService.clearCache();
    this.getRatings();
  }

  ratingAdd(): void {
    if (!this.navigation.getParamValue('ratings.addInDialog', true)) {
      this.router.navigate(['/ratings/add']);
      return;
    }
    this.ratingAddDialog = this.dialogService.open(DialogRatingFormComponent, {
      header: '', // this.translate.instant('NEWRATING'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        rating: {id: null},
        users: this.ratingsColumnsService.users,
      },
    });

    this.ratingAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Rating added'});
        this.getRatings();
      }
    });
  }

  ratingEdit(rating: Rating): void {
    if (!this.navigation.getParamValue('ratings.editInDialog', true)) {
      this.router.navigate(['/ratings/edit/' + rating.id]);
      return;
    }
    this.ratingEditDialog = this.dialogService.open(DialogRatingFormComponent, {
      header: '', // this.translate.instant('EDITRATING') + ' ' + rating.entity,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        rating: rating,
        users: this.ratingsColumnsService.users,
      },
    });

    this.ratingEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Rating updated'});
        this.getRatings();
      }
    });
  }

  ratingDetails(rating: Rating): void {
    if (!this.navigation.getParamValue('ratings.detailsInDialog', true)) {
      this.router.navigate(['/ratings/details/' + rating.id]);
      return;
    }
    this.ratingDetailsDialog = this.dialogService.open(DialogRatingDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        rating: rating,
      }
    });
  }

  ratingDelete(rating: Rating): void {
    this.ratingToDelete = rating;
    this.messageService.clear();
    this.messageService.add({
      key: 'ratingDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + rating.entity + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.ratingsDataService.deleteRating(this.ratingToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Rating deleted'});
      this.getRatings();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Rating not deleted'});
    });
  }

  onDeleteReject(): void {
    this.ratingToDelete = {};
    this.messageService.clear('ratingDelete');
  }

}
